<template>
  <div :class="b()">
    <div :class="b('header')">
      <h1 :class="b('title')">Album Templates</h1>
      <a-button type="primary" :class="b('create-btn')" @click="showCreateModal">
        Create New Template
      </a-button>
    </div>

    <a-spin :spinning="loading">
      <div :class="b('filters')">
        <a-radio-group v-model:value="selectedCategory" button-style="solid">
          <a-radio-button value="all">All Templates</a-radio-button>
          <a-radio-button value="wedding">Wedding</a-radio-button>
          <a-radio-button value="family">Family</a-radio-button>
          <a-radio-button value="travel">Travel</a-radio-button>
          <a-radio-button value="custom">Custom</a-radio-button>
        </a-radio-group>
      </div>

      <div :class="b('list')">
        <a-row :gutter="[16, 16]">
          <a-col
            v-for="template in filteredTemplates"
            :key="template.id"
            :xs="24"
            :sm="12"
            :md="8"
            :lg="6"
          >
            <a-card
              :class="b('card')"
              hoverable
              :cover="template.thumbnail_url"
            >
              <template #cover>
                <img
                  :alt="template.name"
                  :src="template.thumbnail_url || '/placeholder.png'"
                  :class="b('card-image')"
                />
              </template>
              <a-card-meta
                :title="template.name"
                :description="`Category: ${template.category}`"
              />
              <div :class="b('card-footer')">
                <a-space>
                  <a-button size="small" @click="handleEdit(template)">Edit</a-button>
                  <a-button
                    size="small"
                    danger
                    @click="handleDelete(template)"
                  >
                    Delete
                  </a-button>
                  <a-switch
                    v-model:checked="template.is_active"
                    checked-children="Active"
                    un-checked-children="Inactive"
                    @change="handleToggleActive(template)"
                  />
                </a-space>
              </div>
            </a-card>
          </a-col>
        </a-row>

        <a-empty
          v-if="!filteredTemplates.length && !loading"
          description="No templates found"
        />
      </div>
    </a-spin>

    <a-modal
      v-model:visible="modalVisible"
      :title="isEditMode ? 'Edit Template' : 'Create New Template'"
      width="95vw"
      :style="{ top: '20px' }"
      :body-style="{ height: '80vh', overflow: 'auto', padding: '16px' }"
      @ok="handleSubmit"
      @cancel="handleCancel"
    >
      <a-form
        ref="formRef"
        :model="formData"
        layout="vertical"
      >
        <a-row :gutter="16">
          <a-col :span="6">
            <a-form-item
              label="Template Name"
              name="name"
              :rules="[{ required: true, message: 'Please input template name!' }]"
            >
              <a-input v-model:value="formData.name" placeholder="Enter template name" />
            </a-form-item>
          </a-col>

          <a-col :span="6">
            <a-form-item
              label="Category"
              name="category"
              :rules="[{ required: true, message: 'Please select category!' }]"
            >
              <a-select v-model:value="formData.category" placeholder="Select category">
                <a-select-option value="wedding">Wedding</a-select-option>
                <a-select-option value="family">Family</a-select-option>
                <a-select-option value="travel">Travel</a-select-option>
                <a-select-option value="custom">Custom</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col :span="6">
            <a-form-item
              label="Thumbnail URL"
              name="thumbnailUrl"
            >
              <a-input v-model:value="formData.thumbnailUrl" placeholder="Enter thumbnail URL" />
            </a-form-item>
          </a-col>

          <a-col :span="6">
            <a-form-item
              label="Is Active"
              name="isActive"
            >
              <a-switch v-model:checked="formData.isActive" />
            </a-form-item>
          </a-col>
        </a-row>

        <a-divider>Page Layouts</a-divider>

        <TemplateBuilder v-model:value="formData.pageLayouts" />
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import bem from '@/utils/bem';
import { mapActions } from 'vuex';
import TemplateBuilder from './TemplateBuilder.vue';

const b = bem('album-templates-list');

export default {
  name: 'AlbumTemplatesList',
  components: {
    TemplateBuilder,
  },
  data() {
    return {
      selectedCategory: 'all',
      modalVisible: false,
      isEditMode: false,
      formData: {
        name: '',
        category: '',
        thumbnailUrl: '',
        pageLayouts: [],
        isActive: true,
      },
      currentTemplateId: null,
    };
  },
  computed: {
    templates() {
      return this.$store.state.albums.templates;
    },
    loading() {
      return this.$store.state.albums.isLoading;
    },
    error() {
      return this.$store.state.albums.error;
    },
    filteredTemplates() {
      if (this.selectedCategory === 'all') {
        return this.templates;
      }
      return this.templates.filter((t) => t.category === this.selectedCategory);
    },
  },
  mounted() {
    this.loadTemplates();
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions('albums', [
      'getTemplates',
      'createTemplate',
      'updateTemplate',
      'deleteTemplate',
    ]),
    async loadTemplates() {
      try {
        await this.getTemplates();
      } catch (error) {
        // Error notification already shown in action
      }
    },
    showCreateModal() {
      this.isEditMode = false;
      this.currentTemplateId = null;
      this.formData = {
        name: '',
        category: '',
        thumbnailUrl: '',
        pageLayouts: [],
        isActive: true,
      };
      this.modalVisible = true;
    },
    handleEdit(template) {
      this.isEditMode = true;
      this.currentTemplateId = template.id;
      const layouts = Array.isArray(template.page_layouts)
        ? JSON.parse(JSON.stringify(template.page_layouts))
        : [];
      this.formData = {
        name: template.name,
        category: template.category,
        thumbnailUrl: template.thumbnail_url,
        pageLayouts: layouts,
        isActive: template.is_active,
      };
      this.modalVisible = true;
    },
    async handleDelete(template) {
      this.$confirm({
        title: 'Are you sure you want to delete this template?',
        content: `Template: ${template.name}`,
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: async () => {
          try {
            await this.deleteTemplate(template.id);
          } catch (error) {
            // Error notification already shown in action
          }
        },
      });
    },
    async handleToggleActive(template) {
      const newStatus = template.is_active;
      const templateIndex = this.templates.findIndex((t) => t.id === template.id);
      try {
        await this.updateTemplate({
          id: template.id,
          templateData: {
            isActive: newStatus,
          },
        });
      } catch (error) {
        // Error notification already shown in action, revert state
        if (templateIndex !== -1) {
          this.templates[templateIndex].is_active = !newStatus;
        }
      }
    },
    async handleSubmit() {
      try {
        await this.$refs.formRef.validate();

        const templateData = {
          name: this.formData.name,
          category: this.formData.category,
          thumbnailUrl: this.formData.thumbnailUrl,
          pageLayouts: this.formData.pageLayouts,
          isActive: this.formData.isActive,
        };

        if (this.isEditMode) {
          await this.updateTemplate({
            id: this.currentTemplateId,
            templateData,
          });
        } else {
          await this.createTemplate(templateData);
        }

        this.modalVisible = false;
      } catch (error) {
        if (error.errorFields) {
          // Validation error - form is invalid
        }
        // Error notification already shown in action
      }
    },
    handleCancel() {
      this.modalVisible = false;
    },
  },
};
</script>

<style lang="scss" src="./style.scss" scoped />
